import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Chip,
  Stack,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  Typography,
  FormControl,
  Autocomplete,
  OutlinedInput,
  FormHelperText,
} from '@mui/material';

import { usePermission } from 'src/hooks/usePermission';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { scrollbarStyle } from 'src/sections/style';

import { EMAIL_REGEX } from '../../../utils/regex';
import { getUsersByDepartments } from '../../../services/user.service';

export default function AddStatus({
  hideModal,
  emailsList,
  departmentList,
  role,
  addStatus,
  currentUserDepartment,
}) {
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();

  const [color, setColor] = useState('#4A3DFF');
  const [emails, setEmails] = useState([]);
  const [emailValid, setEmailValid] = useState(true);
  const [emailSuggestions, setEmailSuggestions] = useState([]);

  const { can } = usePermission();

  const isAuthorized =
    can({ permission: 'TICKET_STATUS:CREATE' }) ||
    can({ permission: 'TICKET_STATUS:UPDATE' });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
    setValue,
  } = useForm({
    mode: 'all',
    defaultValues: {
      description: '',
      name: '',
      systemMappedStatus: '',
      // Ensure the initial value is an array of numbers
      departmentIds: currentUserDepartment ? [Number(currentUserDepartment)] : [],
    },
  });

  const watchedDepts = watch('departmentIds');
  const selectedDepts = useMemo(() => watchedDepts || [], [watchedDepts]);
  const isGlobalSelected = false; // Always false now that global ID 0 is removed

  const handleDepartmentChange = (event) => {
    const {
      target: { value },
    } = event;
    let newSelection = typeof value === 'string' ? value.split(',') : value;
    newSelection = newSelection.map((id) => Number(id));

    setValue('departmentIds', newSelection, { shouldValidate: true });
  };

  useEffect(() => {
    if (selectedDepts.length > 0 && !isGlobalSelected) {
      getUsersByDepartments(selectedDepts).then((res) =>
        setEmailSuggestions(res.filter((u) => !u.archived).map((u) => u.email))
      );
    } else {
      setEmailSuggestions(emailsList || []);
    }
  }, [selectedDepts, emailsList, isGlobalSelected]);

  const onSubmit = (data) => {
    const flatCommand = {
      name: data.name,
      description: data.description,
      systemMappedStatus: data.systemMappedStatus,
      color,
      correspondences: emails,
      departmentIds: data.departmentIds.map((id) => Number(id)),
    };

    addStatus(flatCommand);
    reset();
    hideModal();
  };

  const handleAddEmail = (event, newValue) => {
    const allValid = newValue.every((email) => EMAIL_REGEX.test(email));
    if (allValid) {
      setEmailValid(true);
      setEmails(newValue);
    } else if (newValue.length !== 0) {
      setEmailValid(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack my={2} spacing={2.5}>
        <TextField
          fullWidth
          label={t('tableHeads.status.name')}
          {...register('name', { required: true })}
          error={!!errors.name}
          size={isSmallScreen ? 'small' : 'medium'}
        />

        <TextField
          fullWidth
          multiline
          minRows={2}
          label={t('tableHeads.status.description')}
          {...register('description', { required: true })}
          error={!!errors.description}
          size={isSmallScreen ? 'small' : 'medium'}
        />

        {isAuthorized && (
          <FormControl
            fullWidth
            error={!!errors.departmentIds}
            size={isSmallScreen ? 'small' : 'medium'}
          >
            <InputLabel id="dept-select-label">{t('attributes.department')}</InputLabel>
            <Select
              labelId="dept-select-label"
              multiple
              value={selectedDepts}
              onChange={handleDepartmentChange}
              input={<OutlinedInput label={t('attributes.department')} />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((val) => (
                    <Chip
                      key={val}
                      label={
                        departmentList?.find((d) => Number(d.id) === Number(val))?.name
                      }
                      size="small"
                    />
                  ))}
                </Box>
              )}
              MenuProps={{ PaperProps: { sx: scrollbarStyle } }}
            >
              {departmentList?.map((dep) => (
                <MenuItem key={dep.id} value={Number(dep.id)}>
                  {dep.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {!isGlobalSelected && (
          <FormControl
            fullWidth
            error={!!errors.systemMappedStatus}
            size={isSmallScreen ? 'small' : 'medium'}
          >
            <InputLabel id="system-state-label">
              {t('tableHeads.status.systemState')}
            </InputLabel>
            <Select
              labelId="system-state-label"
              label={t('tableHeads.status.systemState')}
              defaultValue=""
              {...register('systemMappedStatus', { required: !isGlobalSelected })}
            >
              {['UNASSIGNED', 'OPEN', 'ONHOLD', 'RESOLVED', 'CLOSED'].map((state) => (
                <MenuItem key={state} value={state}>
                  {t(`status.${state}`)}
                </MenuItem>
              ))}
            </Select>
            {errors.systemMappedStatus && (
              <FormHelperText error>{t('formControl.fieldRequired')}</FormHelperText>
            )}
          </FormControl>
        )}

        <Autocomplete
          multiple
          freeSolo
          options={emailSuggestions}
          value={emails}
          onChange={handleAddEmail}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('tableHeads.status.correspondances')}
              error={!emailValid}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option}
                {...getTagProps({ index })}
                size="small"
                key={index}
              />
            ))
          }
        />

        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle2">{t('tableHeads.status.color')}:</Typography>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            style={{
              cursor: 'pointer',
              width: 40,
              height: 25,
              border: 'none',
              borderRadius: 4,
            }}
          />
        </Stack>
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        disabled={!isValid || !emailValid || selectedDepts.length === 0}
        sx={{ mt: 2 }}
      >
        {t('pages.statusPage.addStatus')}
      </LoadingButton>
    </form>
  );
}

AddStatus.propTypes = {
  hideModal: PropTypes.func,
  emailsList: PropTypes.array,
  departmentList: PropTypes.array,
  role: PropTypes.any,
  addStatus: PropTypes.func,
  currentUserDepartment: PropTypes.number,
};
